import {WSPIcon} from '@mintdata-wsp/resources/icons/WSPIcon';
import {WSPIconName, WSPIconSize} from '@mintdata-wsp/resources/icons/WSPIcons';
import {ToolbarButtonBehavior} from '@mintdata-wsp/wsp/ui/components/general/Toolbar';
import {BaseComponentWithContext} from '@mintdata/mint-kit/api/components/BaseComponentWithContext';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {Popover} from '@mintdata/mint-kit/impl/components/popover/Popover';
import {IPopupManager, PopupAnchor, SourceAnchor} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./ToolbarButton.less');
const cx = classnames.bind(styles);

export enum TooltipSide
{
    North,
    East,
    South,
    West
}

export interface ToolbarButtonProps
{
    iconName:WSPIconName;
    iconSize?:WSPIconSize;
    text?:string;
    onClick?:(e:React.SyntheticEvent<any>)=>(void);
    className?:string;
    iconClassName?:string;
    textClassName?:string;
    style?:any;
    tooltip?:string;
    tooltipSide?:TooltipSide;
    popupContent?:React.ReactElement<any>;
    behavior?:ToolbarButtonBehavior;
    isToggledOn?:boolean;
}

@observer
export class ToolbarButton extends BaseComponentWithContext<ToolbarButtonProps, any>
{
    private _popupId:GUID = IdUtil.newGUID();

    @observable
    private _isMouseOver:boolean = false;

    @observable
    private _extraClassName:string = null;

    constructor(props:any)
    {
        super(props);
    }

    _renderMessage()
    {
        return <div className={cx('tooltipMessage')}>
            {this.props.tooltip}
        </div>
    }

    _renderArrowDown()
    {
        return <div className={cx('arrowDown')}/>;
    }

    _renderArrowUp()
    {
        return <div className={cx('arrowUp')}/>;
    }

    @bind
    _renderTooltipPopover()
    {
        const {tooltipSide} = this.props;

        if( !this._isMouseOver )
            return null;

        return <Popover id={this._popupId} isModal={false}>

                    <div className={cx('tooltipContainer')}>

                        {tooltipSide === TooltipSide.North?this._renderMessage():null}
                        {tooltipSide === TooltipSide.North?this._renderArrowDown():null}

                        {tooltipSide === TooltipSide.South?this._renderArrowUp():null}
                        {tooltipSide === TooltipSide.South?this._renderMessage():null}

                    </div>

               </Popover>;
    }

    @bind
    _computePopupLayout():{source:SourceAnchor, popup:PopupAnchor}
    {
        const {tooltipSide = TooltipSide.North} = this.props;
        switch(tooltipSide)
        {
            case TooltipSide.North:
                return {source:SourceAnchor.CenterTop, popup:PopupAnchor.CenterBottom};
            case TooltipSide.South:
                return {source:SourceAnchor.CenterBottom, popup:PopupAnchor.CenterTop};
            case TooltipSide.East:
                return {source:SourceAnchor.RightMiddle, popup:PopupAnchor.LeftMiddle};
            case TooltipSide.West:
                return {source:SourceAnchor.LeftMiddle, popup:PopupAnchor.RightMiddle};
        }

        return {source:SourceAnchor.CenterTop, popup:PopupAnchor.CenterBottom};
    }
    @action.bound
    _onMouseEnter()
    {
        this._isMouseOver = true;
    }

    @action.bound
    _onMouseLeave()
    {
        this._isMouseOver = false;
    }

    @action.bound
    _onClick(e:any)
    {
        this._isMouseOver = false;

        const {onClick=null} = this.props;
        if( onClick && typeof onClick === 'function')
            onClick(e);
    }

    render()
    {
        const {
                iconName,
                text=null,
                className=null,
                iconClassName=null,
                textClassName=null,
                style=null,
                behavior=ToolbarButtonBehavior.Button,
                isToggledOn=false,
                iconSize=WSPIconSize.medium24
            } = this.props;

        const popupManager:IPopupManager = this.context.popupManager;
        const popupLayout = this._computePopupLayout();
        const hasToggleBehavior = behavior === ToolbarButtonBehavior.ToggleOnOff;

        return  <div ref={popupManager.position(this._popupId, popupLayout.source, popupLayout.popup)}
                     onMouseEnter={this._onMouseEnter}
                     onMouseLeave={this._onMouseLeave}
                     onClick={this._onClick}
                     style={style}
                     className={cx('toolbarButton',
                                   {'isMouseOver'   : this._isMouseOver},
                                   {'isToggledOn'   : hasToggleBehavior && isToggledOn},
                                   {'isToggledOff'  : hasToggleBehavior && !isToggledOn},
                                   this._extraClassName,
                                   className)}>

                    <WSPIcon className={cx('icon', iconClassName)} name={iconName} size={iconSize}/>
                    {
                        text?
                            <div className={cx('buttonText', textClassName)}>{text}</div>
                            :
                            null
                    }

                    {this._renderTooltipPopover()}

                </div>;
    }
}
